import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

const baseFlexRow = {
  display: 'flex',
  flexDirection: 'row'
}

const baseTypography = {
  fontFamily: 'Roboto',
  fontSize: 14
}

const tableCell = {
  padding: 10.5,
  fontSize: 14
}

export const theme = {
  paper: { margin: 'auto', width: '15cm' },
  paperInline: { margin: '0 0 0 0', width: '15cm' },
  page: {
    ...baseFlexRow,
    flexDirection: 'column',
    width: '15cm',
    fontSize: 11,
    paddingTop: 20,
    paddingBottom: 44,
    paddingLeft: 40,
    paddingRight: 40,
    lineHeight: 1.5
  },

  spaceBetween: {
    ...baseFlexRow,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#3E3E3E'
  },

  titleContainer: baseFlexRow,
  logo: { width: 120 },
  mt: { marginTop: 10 },

  reportTitle: {
    fontFamily: 'Roboto',
    fontWeight: 300,
    fontSize: 40,
    textAlign: 'center',
    paddingTop: 10
  },

  rightContainer: {
    ...baseFlexRow,
    justifyContent: 'flex-end',
    marginTop: 24
  },

  rightContainerNoMT: {
    ...baseFlexRow,
    justifyContent: 'flex-end'
  },

  boldWithSpace: {
    ...baseTypography,
    fontWeight: 600,
    marginRight: 5
  },

  address: {
    ...baseTypography,
    fontWeight: 400
  },

  bold: {
    ...baseTypography,
    fontWeight: 600
  },

  addressBlock: {
    ...baseFlexRow,
    justifyContent: 'space-between',
    marginTop: 20
  },

  table: {
    ...baseFlexRow,
    marginTop: 10
  },

  tablebody: baseFlexRow,

  theader: {
    ...tableCell,
    ...baseTypography,
    ...baseFlexRow,
    flexBasis: 100,
    marginTop: 20,
    fontWeight: 600,
    alignContent: 'center',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },

  theader2: {
    ...tableCell,
    ...baseTypography,
    ...baseFlexRow,
    flexBasis: 220,
    marginTop: 20,
    fontWeight: 600,
    alignContent: 'center',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },

  tbody: {
    ...tableCell,
    textAlign: 'right',
    flexBasis: 100,
    display: 'flex',
    flexDirection: 'row-reverse'
  },

  tbodyfill: {
    ...tableCell,
    textAlign: 'right',
    flexBasis: 100,
    display: 'flex',
    flexDirection: 'row-reverse',
    backgroundColor: '#0000000d'
  },

  tbody2: {
    ...tableCell,
    flexBasis: 220
  },

  tbodyfill2: {
    ...tableCell,
    flexBasis: 220,
    backgroundColor: '#0000000d'
  },

  tbodytotal: {
    ...tableCell,
    flexBasis: 100,
    textAlign: 'right',
    borderColor: 'whitesmoke',
    borderTopWidth: 1
  },

  total: {
    ...tableCell,
    flexBasis: 150
  },

  conditions: {
    fontSize: 9,
    color: '#666'
  },

  footerText: {
    fontSize: 9,
    textAlign: 'right'
  },

  footerContainer: {
    marginTop: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  }
}

export const Header = ({ logo, classes, documentTitle }) => {
  if (!classes) return null

  return (
    <Box className={classes.titleContainer}>
      <Box className={classes.spaceBetween}>
        {logo !== undefined && logo !== null && logo !== '' ? (
          <Box component='img' className={classes.logo} src={`data:image/png;base64,${logo}`} />
        ) : (
          <Box component='img' className={classes.logo} />
        )}
        <Box className={classes.mt}>
          <Typography className={classes.reportTitle}>{documentTitle}</Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const DocumentDate = ({ date, classes }) => {
  const { t } = useTranslation()
  if (!classes) return null
  if (!date) return null

  const dateConv = t('dates.localisedDate', { date: new Date(date) })
  return (
    <Box className={classes.rightContainer}>
      <Typography className={classes.bold}>{dateConv}</Typography>
    </Box>
  )
}

export const CustomerAddress = ({ customer, company, classes }) => {
  if (!classes) return null
  if (!customer || !company) return null
  return (
    <Box className={classes.addressBlock}>
      <Box>
        <Box>
          <Typography className={classes.bold}>{company.name}</Typography>

          <Typography className={classes.address}>{company.address1}</Typography>
          {company.address2 !== undefined && company.address2 !== null && company.address2 !== '' && (
            <Typography className={classes.address}>{company.address2}</Typography>
          )}

          <Typography className={classes.address}>
            {company.zip} {company.city}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Box>
          <Typography className={classes.bold}>{customer.customer_name}</Typography>

          <Typography className={classes.address}>{customer.address1}</Typography>
          {customer.address2 !== undefined && customer.address2 !== null && customer.address2 !== '' && (
            <Typography className={classes.address}>{customer.address2}</Typography>
          )}

          <Typography className={classes.address}>
            {customer.zip} {customer.city}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const Subject = ({ document, classes }) => {
  if (!classes) return null
  if (!document.summary) return null
  return (
    <Box className={classes.mt}>
      <Typography className={classes.bold}>{document.summary}</Typography>
      {document.description && <Typography className={classes.address}>{document.description}</Typography>}
    </Box>
  )
}

export const TableHeader = ({ headers, classes }) => {
  if (!classes) return null
  if (!headers) return null
  return (
    <Box className={classes.table}>
      {headers.map((header) => (
        <Box key={header.name} className={classes[header.headerStyle]}>
          <Typography>{header.label}</Typography>
        </Box>
      ))}
    </Box>
  )
}

export const TableBody = ({ headers, lines, total, classes }) => {
  if (!classes) return null
  if (!headers || !lines) return null
  return (
    <>
      {lines.map((line, index) => (
        <Box className={classes.tablebody} key={line.id}>
          {headers.map((header) => (
            <Box key={header.name} className={index % 2 === 0 ? classes[header.bodyStyle] : classes[header.bodyStyle2]}>
              <Typography>{line[header.name]}</Typography>
            </Box>
          ))}
        </Box>
      ))}

      {total && (
        <Box className={classes.tablebody}>
          <Box className={classes.total}></Box>
          <Box className={classes.total}></Box>
          <Box className={classes.tbodytotal}>
            <Typography>Total HT</Typography>
          </Box>
          <Box className={classes.tbodytotal}>
            <Typography>{total}€</Typography>
          </Box>
        </Box>
      )}
    </>
  )
}

export const Payment = ({ customer, settings, classes }) => {
  if (!classes) return null
  if (!customer || !settings) return null
  const paymentConditions = customer.customer_type === 2 ? settings.payment_conditions : settings.payment_conditions_pro
  const paymentMethods = customer.customer_type === 2 ? settings.payment_methods : settings.payment_methods_pro
  return (
    <Box className={classes.mt}>
      <Typography className={classes.conditions}>Conditions de paiement : {paymentConditions}</Typography>
      <Typography className={classes.conditions}>Règlement par : {paymentMethods.replaceAll(',', ', ')}</Typography>
    </Box>
  )
}

export const Footer = ({ company, classes }) => {
  if (!classes) return null
  if (!company) return null
  return (
    <>
      <Box className={classes.footerContainer}>
        <Box>
          <Typography
            className={classes.footerText}
          >{`${company.name} - N° SIRET ${company.siret} - Code APE ${company.ape}`}</Typography>
        </Box>
        <Box>
          <Typography
            className={classes.footerText}
          >{`${company.address1} - ${company.zip} ${company.city} - ${company.phone}`}</Typography>
        </Box>
        <Box>
          <Typography className={classes.footerText}>{`${company.email} - ${company.website}`}</Typography>
        </Box>
      </Box>
    </>
  )
}
